import * as THREE from 'three';
import { registerScenePolicy } from '../../../core/ScenePolicies';
import { global } from '../../../core/GlobalState';
import { addSimpleOrbs, addWorldBoundarySphere } from '../worlds/WorldBuilderHelpers';
import * as TextCreation from '../TextCreation';
import { computeRingLayout } from '../../../utils/RingLayout';
import { World } from '../worlds/World';
import * as ObjectCreation from '../ObjectCreation';
function createBond(start, end, radius = 16, color = 0x3aa1ff) {
    const length = start.distanceTo(end);
    const geometry = new THREE.CylinderGeometry(radius, radius, length, 16, 1, false);
    const material = new THREE.MeshStandardMaterial({
        color,
        emissive: 0x0b1a2f,
        metalness: 0.2,
        roughness: 0.4,
    });
    const mesh = new THREE.Mesh(geometry, material);
    const direction = new THREE.Vector3().subVectors(end, start).normalize();
    const quaternion = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction);
    mesh.position.copy(start).add(end).multiplyScalar(0.5);
    mesh.quaternion.copy(quaternion);
    return mesh;
}
function createMoleculeCluster() {
    const group = new THREE.Group();
    const carbonGeometry = new THREE.SphereGeometry(70, 24, 24);
    const carbonMaterial = new THREE.MeshStandardMaterial({
        color: 0x45c7ff,
        emissive: 0x122b3f,
        metalness: 0.35,
        roughness: 0.25,
    });
    const hydrogenGeometry = new THREE.SphereGeometry(42, 18, 18);
    const hydrogenMaterial = new THREE.MeshStandardMaterial({
        color: 0xf3f5ff,
        emissive: 0x1b2738,
        metalness: 0.08,
        roughness: 0.35,
    });
    const ringRadius = 320;
    const carbonPositions = [];
    for (let i = 0; i < 6; i++) {
        const angle = (i / 6) * Math.PI * 2;
        carbonPositions.push(new THREE.Vector3(Math.cos(angle) * ringRadius, 340 + Math.sin(angle * 2) * 20, Math.sin(angle) * ringRadius * 0.7));
    }
    carbonPositions.forEach(position => {
        const carbon = new THREE.Mesh(carbonGeometry, carbonMaterial);
        carbon.position.copy(position);
        group.add(carbon);
    });
    for (let i = 0; i < carbonPositions.length; i++) {
        const next = (i + 1) % carbonPositions.length;
        group.add(createBond(carbonPositions[i], carbonPositions[next]));
    }
    carbonPositions.forEach(position => {
        const direction = position.clone().normalize();
        const hydrogenPos = position.clone().add(direction.multiplyScalar(190));
        const hydrogen = new THREE.Mesh(hydrogenGeometry, hydrogenMaterial);
        hydrogen.position.copy(hydrogenPos);
        group.add(hydrogen);
        group.add(createBond(position, hydrogenPos, 12, 0x58d6ff));
    });
    group.position.y = 40;
    return group;
}
function createSpectrometerArc() {
    const group = new THREE.Group();
    const torus = new THREE.Mesh(new THREE.TorusGeometry(460, 18, 16, 96), new THREE.MeshBasicMaterial({
        color: 0x58f7ff,
        transparent: true,
        opacity: 0.38,
    }));
    torus.rotation.x = Math.PI / 2;
    torus.position.y = 320;
    group.add(torus);
    const frame = new THREE.LineSegments(new THREE.EdgesGeometry(new THREE.TorusGeometry(460, 18, 8, 64)), new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.35 }));
    frame.rotation.x = Math.PI / 2;
    frame.position.copy(torus.position);
    group.add(frame);
    return group;
}
function createFlaskRack() {
    const group = new THREE.Group();
    const base = new THREE.Mesh(new THREE.BoxGeometry(820, 40, 260), new THREE.MeshStandardMaterial({ color: 0x1c2d3f, metalness: 0.55, roughness: 0.38 }));
    base.position.set(0, 20, 0);
    group.add(base);
    const glassMaterial = new THREE.MeshPhysicalMaterial({
        color: 0x7ecfff,
        transparent: true,
        opacity: 0.58,
        transmission: 0.85,
        metalness: 0.1,
        roughness: 0.18,
        clearcoat: 0.3,
    });
    const solutionMaterials = [0xff9a6b, 0x7cf7c2, 0x9b8bff, 0xfff176].map(color => new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 0.2, metalness: 0.05, roughness: 0.4 }));
    for (let i = 0; i < 4; i++) {
        const x = -300 + i * 200;
        const flask = new THREE.Mesh(new THREE.CylinderGeometry(70, 90, 200, 24, 1, false), glassMaterial);
        flask.position.set(x, 160, -60 + (i % 2) * 60);
        group.add(flask);
        const solution = new THREE.Mesh(new THREE.CylinderGeometry(60, 86, 90, 24), solutionMaterials[i]);
        solution.position.set(x, 120, flask.position.z);
        group.add(solution);
    }
    return group;
}
function createParticleMist(count = 900) {
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
        const idx = i * 3;
        positions[idx] = (Math.random() - 0.5) * 900;
        positions[idx + 1] = 260 + Math.random() * 320;
        positions[idx + 2] = (Math.random() - 0.5) * 900;
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    const material = new THREE.PointsMaterial({
        color: 0x9fe9ff,
        size: 16,
        transparent: true,
        opacity: 0.5,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
    });
    const points = new THREE.Points(geometry, material);
    points.frustumCulled = false;
    return points;
}
function createElementPedestal(symbol, name, color) {
    const group = new THREE.Group();
    const base = new THREE.Mesh(new THREE.CylinderGeometry(140, 140, 60, 24), new THREE.MeshStandardMaterial({ color: 0x1b2a3a, metalness: 0.45, roughness: 0.35 }));
    base.position.y = 30;
    group.add(base);
    const glass = new THREE.Mesh(new THREE.CylinderGeometry(120, 120, 220, 48, 1, true), new THREE.MeshPhysicalMaterial({
        color,
        transparent: true,
        opacity: 0.4,
        transmission: 0.8,
        roughness: 0.15,
        thickness: 6,
    }));
    glass.position.y = 170;
    group.add(glass);
    const glyph = TextCreation.createTextSprite(symbol, 2.4, new THREE.Vector3(0, 260, 0), 6, '120px "IBM Plex Sans"', '#d9f5ff', '#102333');
    const label = TextCreation.createTextSprite(name, 1.1, new THREE.Vector3(0, 190, 0), 5, '64px "IBM Plex Sans"', '#9dd9ff', '#142b3a');
    group.add(glyph);
    group.add(label);
    return group;
}
function addElementPedestals(scene) {
    const elements = [
        { symbol: 'He', name: 'Helium', color: 0xffd27f },
        { symbol: 'C', name: 'Carbon', color: 0x7fe0ff },
        { symbol: 'Fe', name: 'Iron', color: 0xff968a },
        { symbol: 'Au', name: 'Gold', color: 0xfff6a3 },
        { symbol: 'Ce', name: 'Cerium', color: 0xb0ffce },
        { symbol: 'U', name: 'Uranium', color: 0x90f7ff },
    ];
    const items = elements.map((element, index) => ({ id: `${element.symbol}-${index}`, radius: 240 }));
    const layout = computeRingLayout(items, {
        centre: new THREE.Vector3(0, 0, 0),
        minRadius: 2800,
        minSeparation: 280,
        startAngle: Math.PI / 3,
    });
    layout.placements.forEach((placement, index) => {
        const element = elements[index];
        if (!element)
            return;
        const pedestal = createElementPedestal(element.symbol, element.name, element.color);
        pedestal.position.copy(placement.position);
        scene.add(pedestal);
    });
}
function addFormulaSprites(scene) {
    const formulas = [
        { text: 'H₂O', position: new THREE.Vector3(-420, 640, -1260) },
        { text: 'C₆H₁₂O₆', position: new THREE.Vector3(420, 680, -1260) },
        { text: 'NaCl', position: new THREE.Vector3(-1260, 640, 420) },
        { text: 'NH₃', position: new THREE.Vector3(1260, 640, 420) },
    ];
    formulas.forEach(entry => {
        const sprite = TextCreation.createTextSprite(entry.text, 2.2, entry.position.clone(), 5, '92px "IBM Plex Sans"', '#b7faff', '#10243a');
        sprite.material.transparent = true;
        sprite.material.opacity = 0.9;
        scene.add(sprite);
    });
}
const SLUG = 'chemistry';
const LABEL = 'Chemistry';
function createIonTrail() {
    const points = [];
    const segments = 64;
    for (let i = 0; i <= segments; i++) {
        const t = i / segments;
        const angle = t * Math.PI * 4;
        const radius = 480 + Math.sin(t * Math.PI) * 180;
        points.push(new THREE.Vector3(Math.cos(angle) * radius, 280 + Math.cos(angle * 0.5) * 160, Math.sin(angle) * radius * 0.6));
    }
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const material = new THREE.LineDashedMaterial({
        color: 0x75e4ff,
        dashSize: 120,
        gapSize: 90,
        linewidth: 2,
    });
    const line = new THREE.Line(geometry, material);
    line.computeLineDistances();
    return line;
}
function createCrystallineMatrix() {
    const geometry = new THREE.DodecahedronGeometry(220, 1);
    const material = new THREE.MeshStandardMaterial({
        color: 0xb8f1ff,
        emissive: 0x1f3650,
        metalness: 0.55,
        roughness: 0.18,
        transparent: true,
        opacity: 0.72,
    });
    const mesh = new THREE.Mesh(geometry, material);
    const edges = new THREE.LineSegments(new THREE.EdgesGeometry(geometry), new THREE.LineBasicMaterial({ color: 0xffffff, opacity: 0.45, transparent: true }));
    mesh.add(edges);
    return mesh;
}
function buildChemistryWorld(context) {
    const { scene } = context;
    scene.background = new THREE.Color(0xf5fbff);
    scene.fog = new THREE.Fog(0xe8f4ff, 0, 22000);
    const labLight = new THREE.HemisphereLight(0xd7f3ff, 0x9fc8ff, 0.85);
    scene.add(labLight);
    const reactorGlow = new THREE.PointLight(0x4dd7ff, 1.9, 5200, 1.4);
    reactorGlow.position.set(0, 760, 0);
    scene.add(reactorGlow);
    const burnerLight = new THREE.PointLight(0xff7a45, 1.1, 2500, 1.6);
    burnerLight.position.set(-420, 320, -260);
    scene.add(burnerLight);
    //scene.add(ObjectCreation.createWorldAxis(20000, 20000, 1000))
    const chemAxis = ObjectCreation.createWorldAxis(20000, 20000, 1000, {
        showGrid: true,
        gridPrimaryColor: 0xb4e1ff,
        gridSecondaryColor: 0x79c6ff,
        gridOpacity: 0.32,
        gridYOffset: 0.05,
    });
    scene.add(chemAxis);
    addSimpleOrbs(scene, [
        { type: 'TEXT_OBJECT_ORB', label: 'Periodic Atlas', position: new THREE.Vector3(-2000, 360, -800) },
        { type: 'MATH_ORB', label: 'Reaction Graph', position: new THREE.Vector3(2000, 360, -800) },
        { type: 'PROMPT_ORB', label: 'Lab Notebook', position: new THREE.Vector3(0, 520, 2100) },
        { type: 'API_ORB', label: 'Reagent API', position: new THREE.Vector3(0, 360, -2100) },
    ]);
    const molecule = createMoleculeCluster();
    molecule.position.set(-2200, 120, 0);
    scene.add(molecule);
    const spectrometer = createSpectrometerArc();
    spectrometer.position.set(2200, 80, 0);
    scene.add(spectrometer);
    const rack = createFlaskRack();
    rack.position.set(0, 120, -2200);
    scene.add(rack);
    const mist = createParticleMist();
    scene.add(mist);
    const ionTrail = createIonTrail();
    ionTrail.position.set(0, 60, 0);
    scene.add(ionTrail);
    const crystalMatrix = createCrystallineMatrix();
    crystalMatrix.position.set(0, 420, 0);
    scene.add(crystalMatrix);
    addElementPedestals(scene);
    addFormulaSprites(scene);
    registerScenePolicy(SLUG, { radius: 14000, floorY: 0 });
    addWorldBoundarySphere(scene, 14000, { color: 0x87c8ff, opacity: 0.18, segments: 64 });
}
export const ChemistryWorld = new World({
    slug: SLUG,
    label: LABEL,
    build: buildChemistryWorld,
});
global.worldRegistry.registerWorld(ChemistryWorld);
